import React from 'react'
import styled, { css } from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

export const EditModeColor = CantileverTheme.colors.blue['100']

export type HudButtonAnimation = 'rotate' | 'scale' | 'none'

interface HudButtonContainerProps {
  $selected: boolean
  $animation?: HudButtonAnimation
  $rotationDegrees?: number
  $scaleAmount?: number
  $hasBorder?: boolean
  $small?: boolean
}

const HudButtonContainer = styled.div<HudButtonContainerProps>`
  cursor: pointer;
  width: ${({ $selected, $small }) =>
    $small ? ($selected ? '24px' : '24px') : $selected ? '56px' : '48px'};
  height: ${({ $selected, $small }) =>
    $small ? ($selected ? '24px' : '24px') : $selected ? '56px' : '48px'};
  border-radius: 50%;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.forecast.secondaryText : theme.forecast.background2_5};
  color: ${({ $selected, theme }) =>
    $selected ? theme.forecast.background2_5 : theme.forecast.secondaryText};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    width 200ms,
    height 200ms,
    transform 400ms,
    background-color 200ms,
    color 200ms;

  ${({ $animation, $selected, $rotationDegrees = 90, $scaleAmount = 1.1 }) => {
    switch ($animation) {
      case 'rotate':
        return css`
          transform: ${$selected
            ? `rotate(${$rotationDegrees}deg)`
            : 'rotate(0)'};
        `
      case 'scale':
        return css`
          transform: ${$selected ? `scale(${$scaleAmount})` : 'scale(1)'};
        `
      default:
        return ''
    }
  }}
  ${({ $hasBorder }) =>
    $hasBorder !== false &&
    css`
      border: 0.2rem solid ${EditModeColor};
    `}
  &:hover {
    background-color: ${({ $selected, theme }) =>
      $selected ? theme.forecast.defaultText : theme.forecast.background3};
    ${({ $small }) =>
      !$small &&
      css`
        width: 56px;
        height: 56px;
      `}
  }
`

const IconWrapper = styled.div<{ $iconSize?: string; $small?: boolean }>`
  width: ${({ $iconSize, $small }) => $iconSize || ($small ? '100%' : '70%')};
  height: ${({ $iconSize, $small }) => $iconSize || ($small ? '100%' : '70%')};
  display: flex;
  align-items: center;
  justify-content: center;

  > * {
    width: 100%;
    height: 100%;
  }
`

interface HudButtonProps {
  icon: React.ReactNode
  isSelected: boolean
  onClick: () => void
  animation?: HudButtonAnimation
  rotationDegrees?: number
  scaleAmount?: number
  hasBorder?: boolean
  iconSize?: string
  small?: boolean
  className?: string
  ariaLabel?: string
}

export const HudButton: React.FC<HudButtonProps> = ({
  icon,
  isSelected,
  onClick,
  animation = 'none',
  rotationDegrees,
  scaleAmount,
  hasBorder = true,
  iconSize,
  small = false,
  className,
  ariaLabel,
}) => {
  return (
    <HudButtonContainer
      $selected={isSelected}
      $animation={animation}
      $rotationDegrees={rotationDegrees}
      $scaleAmount={scaleAmount}
      $hasBorder={hasBorder}
      $small={small}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      role='button'
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={className}
      data-selected={isSelected}>
      <IconWrapper $iconSize={iconSize} $small={small}>
        {icon}
      </IconWrapper>
    </HudButtonContainer>
  )
}
